import React, { useState, useEffect } from 'react';
import { Button, Menu, MenuItem, Divider } from '@mui/material';
import { ArrowDropDown, AutoAwesome, Folder, Clear } from '@mui/icons-material';
import MaterialPicker from '../materials/MaterialPicker';
import type { MaterialWithFileMetadata } from '../../types';

interface ContextReferenceSelectorProps {
  lang: string;
  projectId: string;
  size?: 'small' | 'medium' | 'large';
  onFileIdsChange?: (fileIds?: string[]) => void;
}

function ContextReferenceSelector({ 
  lang, 
  projectId, 
  size = 'medium',
  onFileIdsChange 
}: ContextReferenceSelectorProps) {
  const [contextMode, setContextMode] = useState<'auto' | 'manual'>('auto');
  const [selectedMaterials, setSelectedMaterials] = useState<MaterialWithFileMetadata[]>([]);
  const [contextMenuAnchorEl, setContextMenuAnchorEl] = useState<HTMLElement | null>(null);


  // Notify parent when file IDs change
  useEffect(() => {
    if (onFileIdsChange) {
      const fileIds = contextMode === 'manual' 
        ? selectedMaterials.map(m => m.fileServiceFileId)
        : undefined;
      onFileIdsChange(fileIds);
    }
  }, [contextMode, selectedMaterials, onFileIdsChange]);

  const handleOpenContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    setContextMenuAnchorEl(event.currentTarget);
  };

  const handleCloseContextMenu = () => {
    setContextMenuAnchorEl(null);
  };

  const handleAutoReference = () => {
    setContextMode('auto');
    setSelectedMaterials([]);
    setContextMenuAnchorEl(null);
  };



  const handleSelectMaterials = (materials: MaterialWithFileMetadata[]) => {
    setSelectedMaterials(materials);
    if (materials.length === 0) {
      setContextMode('auto');
    }

  };

  const handleNoReference = () => {
    setContextMode('manual');
    setSelectedMaterials([]);
    setContextMenuAnchorEl(null);
  };

  const getContextButtonLabel = () => {
    if (contextMode === 'auto') {
      return lang === 'en' ? 'Auto Reference Materials' : '自动选择参考材料';
    } else if (contextMode === 'manual' && selectedMaterials.length === 0) {
      return lang === 'en' ? 'Do not use reference materials' : '不使用参考材料';
    } 
    else {
      const count = selectedMaterials.length;
      return lang === 'en' 
        ? `${count} file${count !== 1 ? 's' : ''} selected as context reference`
        : `已选择 ${count} 个文件作为上下文参考`;
    }
  };

  return (
    <>
      <Button 
        variant="outlined" 
        size={size}
        onClick={handleOpenContextMenu}
        endIcon={<ArrowDropDown />}
        startIcon={contextMode === 'auto' ? <AutoAwesome /> : contextMode === 'manual' && selectedMaterials.length === 0 ? <Clear /> : <Folder />}
      >
        {getContextButtonLabel()}
      </Button>

      <Menu
        anchorEl={contextMenuAnchorEl}
        open={Boolean(contextMenuAnchorEl)}
        onClose={handleCloseContextMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleAutoReference}>
          <AutoAwesome sx={{ mr: 1 }} fontSize="small" color="action" />
          {lang === 'en' ? 'Auto Reference Materials' : '自动选择参考材料'}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleNoReference}>
          <Clear sx={{ mr: 1 }} fontSize="small" color="action" />
          {lang === 'en' ? 'Do not use reference materials' : '不使用参考材料'}
        </MenuItem>
        <MaterialPicker
          onSelect={handleSelectMaterials}
          multiple={true}
          projectId={projectId}
          title={lang === 'en' ? 'Select Materials for Context' : '选择上下文材料'}
          initialSelected={selectedMaterials.map(m => m.fileServiceFileId)}
          lang={lang as 'en' | 'zh-CN' | 'zh-HK'}
        >
          {open => (
            <MenuItem onClick={open}>
              <Folder sx={{ mr: 1 }} fontSize="small" color="action" />
              {lang === 'en' ? 'Select Materials...' : '选择材料...'}
            </MenuItem>
          )}
        </MaterialPicker>
      </Menu>

    </>
  );
}

export default ContextReferenceSelector; 